import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import BlacksmithWorkshop from '../components/BlacksmithWorkshop';
import { createICSpicyActor } from '../services/icspicy';
import { Principal } from '@dfinity/principal';
import { useAuth } from '../auth/useAuth';

interface Analytics {
  total_nfts: number;
  total_users: number;
  timestamp: bigint;
}

export default function AdminPage() {
  const { agent, authClient, principal, isAuthenticated, loginWithII } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminConfig, setAdminConfig] = useState<any>(null);
  const [targetCanisterId, setTargetCanisterId] = useState('');
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (!agent || !isAuthenticated || !principal) return;
      try {
        const actor = await createICSpicyActor(agent);
        const role = await actor.get_user_role_query(principal);
        const admin = !!(role && role.length > 0 && role[0].hasOwnProperty('Admin'));
        setIsAdmin(admin);
        if (admin) {
          await loadAdminConfig(actor);
          await loadAnalytics(actor);
        }
      } catch (err) {
        console.error('Failed to init admin page:', err);
      }
    };
    init();
  }, [agent, isAuthenticated, principal]);

  const loadAnalytics = async (actor: any) => {
    setLoadingAnalytics(true);
    try {
      const analyticsData = await actor.get_analytics();
      if (analyticsData && analyticsData.length > 0) {
        setAnalytics(JSON.parse(analyticsData[0]));
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const loadAdminConfig = async (actor: any) => {
    try {
      const config = await actor.get_admin_config();
      setAdminConfig(config);
      setTargetCanisterId(config.target_canister_id.toText());
    } catch (err) {
      console.error('Failed to load config:', err);
    }
  };

  const handleConnect = async () => {
    try {
      await loginWithII();
    } catch (err: any) {
      setError(err.message || 'Failed to connect');
    }
  };

  const handleUpdateTargetCanister = async () => {
    if (!agent || !authClient || !isAdmin) return;

    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const actor = await createICSpicyActor(agent);
      const canisterId = Principal.fromText(targetCanisterId);
      await actor.set_target_canister(canisterId);
      setSuccess('Target canister updated successfully');
      loadAdminConfig(actor);
    } catch (err: any) {
      setError(err.message || 'Failed to update target canister');
    } finally {
      setUpdating(false);
    }
  };

  const handleInstallStandards = async () => {
    if (!agent || !authClient || !isAdmin) return;

    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const actor = await createICSpicyActor(agent);
      await actor.install_standards();
      setSuccess('ICRC7/ICRC37 and EXT standards installed');
      loadAdminConfig(actor);
    } catch (err: any) {
      setError(err.message || 'Failed to install standards');
    } finally {
      setUpdating(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <BlacksmithWorkshop>
        <div className="container mx-auto px-4 py-8">
          <div className="glass-dark rounded-2xl p-8 max-w-md mx-auto text-center">
            <h2 className="text-2xl font-semibold mb-4">Admin Panel</h2>
            <button
              onClick={handleConnect}
              className="bg-gold hover:bg-yellow-600 text-white font-bold py-3 px-6 rounded-lg transition-all"
            >
              Connect Wallet
            </button>
          </div>
        </div>
      </BlacksmithWorkshop>
    );
  }

  if (!isAdmin) {
    return (
      <BlacksmithWorkshop>
        <div className="container mx-auto px-4 py-8">
          <div className="glass-dark rounded-2xl p-8 max-w-md mx-auto text-center">
            <h2 className="text-2xl font-semibold mb-4 text-red-400">Access Denied</h2>
            <p className="text-gray-300">Admin access required</p>
            <p className="text-sm text-gray-400 mt-2">Your Principal: {principal?.toText()}</p>
          </div>
        </div>
      </BlacksmithWorkshop>
    );
  }

  return (
    <BlacksmithWorkshop>
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          <div className="glass-dark rounded-2xl p-8 mb-6">
            <h1 className="text-4xl font-bold mb-6 text-gold">Admin Panel</h1>
            
            <div className="mb-6">
              <h2 className="text-2xl font-semibold mb-4">Admin Principal ID</h2>
              <p className="text-sm font-mono break-all bg-black/30 p-3 rounded">
                {principal?.toText()}
              </p>
            </div>

            {/* Analytics Dashboard */}
            {analytics && (
              <div className="mb-6 p-4 bg-black/30 rounded-lg">
                <h2 className="text-xl font-semibold mb-4 text-gold">Analytics</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400">Total NFTs</p>
                    <p className="text-2xl font-bold text-gold">{analytics.total_nfts}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Total Users</p>
                    <p className="text-2xl font-bold text-gold">{analytics.total_users}</p>
                  </div>
                </div>
              </div>
            )}

            {adminConfig && (
              <div className="mb-6">
                <h2 className="text-2xl font-semibold mb-4">Canister Configuration</h2>
                
                <div className="mb-4">
                  <label className="block text-gray-300 mb-2">Target Canister ID</label>
                  <input
                    type="text"
                    value={targetCanisterId}
                    onChange={(e) => setTargetCanisterId(e.target.value)}
                    placeholder="hj7yv-6aaaa-aaaac-a5ciq-cai"
                    className="w-full px-4 py-2 rounded-lg bg-black/30 text-white border border-gray-600"
                  />
                  <button
                    onClick={handleUpdateTargetCanister}
                    disabled={updating}
                    className="mt-2 bg-gold hover:bg-yellow-600 disabled:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-all"
                  >
                    {updating ? 'Updating...' : 'Update Target Canister'}
                  </button>
                </div>

                <div className="mb-4 p-4 bg-black/30 rounded-lg">
                  <h3 className="text-lg font-semibold mb-2">Standards Installation Status</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">ICRC7:</span>
                      <span className={adminConfig.icrc7_installed ? "text-green-400" : "text-red-400"}>
                        {adminConfig.icrc7_installed ? "✓ Installed" : "✗ Not Installed"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">ICRC37:</span>
                      <span className={adminConfig.icrc37_installed ? "text-green-400" : "text-red-400"}>
                        {adminConfig.icrc37_installed ? "✓ Installed" : "✗ Not Installed"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">EXT:</span>
                      <span className={adminConfig.ext_installed ? "text-green-400" : "text-red-400"}>
                        {adminConfig.ext_installed ? "✓ Installed" : "✗ Not Installed"}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleInstallStandards}
                    disabled={updating}
                    className="mt-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition-all"
                  >
                    {updating ? 'Installing...' : 'Install Standards'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-4 bg-red-900/30 border border-red-500 rounded-lg text-red-200 mb-4"
            >
              {error}
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-4 bg-green-900/30 border border-green-500 rounded-lg text-green-200"
            >
              {success}
            </motion.div>
          )}
        </motion.div>
      </div>
    </BlacksmithWorkshop>
  );
}

